import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  KindChip, ReviewPill, Avatar, Icon, ghostBtn, primaryBtn,
} from "@/components/admin-ui";
import { Modal, DateTimeField, useToast } from "@/components/ui-prims";
import { AuthorDrawer } from "@/components/AuthorDrawer";
import {
  ARTICLES, AUTHORS, REVIEW_ACTIVITY, EDITORIAL_CHECKS, type ActivityEvent, type AuthorKey,
} from "@/lib/admin-mock";
import { useAdminActions, useAdminArticle } from "@/lib/articles-hooks";
import { HOUSE_AUTHOR_ID } from "@/constants/articles";

export function ArticleReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  // Detail hook hits /admin/articles/{id} in live mode, mock fallback
  // otherwise. The legacy ARTICLES fallback keeps the page renderable
  // for unknown ids (the original used "a-2026-05-12-a" as the
  // hard-coded default).
  const { detail, refresh } = useAdminArticle(id);
  const a =
    detail?.row ??
    ARTICLES.find((x) => x.id === id) ??
    ARTICLES.find((x) => x.id === "a-2026-05-12-a")!;
  // Prefer the inline author snippet the backend joins onto the
  // article — falls back to the AUTHORS mock map only when the wire
  // didn't carry it (offline mock mode).
  const author = a.authorDisplay ?? AUTHORS[a.author as AuthorKey] ?? AUTHORS.clarissa;
  const authorId = a.authorDisplay?.id ?? null;
  // The "ProProp Data Lab" house identity is not in the /admin/authors
  // roster, so its name must not be a link to the AuthorDrawer (which
  // would 404). Render it as plain text instead (PROP-54).
  const isHouseAuthor = authorId === HOUSE_AUTHOR_ID;

  const actions = useAdminActions();
  const [note, setNote] = useState("");
  const [activityNote, setActivityNote] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [scheduling, setScheduling] = useState(false);
  const [openAuthor, setOpenAuthor] = useState(false);

  // Wrap each moderation action with a toast + redirect on success.
  // Approve/Request-changes/Reject all return the reviewer to the
  // queue; staying on the article after a terminal action confuses
  // reviewers about whether anything happened.
  async function runAndExit(key: string, fn: () => Promise<unknown>, successMsg: string) {
    if (busy) return;
    setBusy(key);
    try {
      await fn();
      toast(successMsg, { tone: "success" });
      navigate("/editorial/review");
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      console.warn(`admin article action failed: ${key}`, e);
      toast(`Action failed: ${msg}`, { tone: "error" });
      setBusy(null);
    }
  }

  const onApproveNow = () =>
    void runAndExit(
      "approve-now",
      () => actions.approve(a.id, { publish: "now" }),
      "Article approved and published",
    );

  const onApproveSchedule = async (whenIso: string) => {
    setScheduling(false);
    await runAndExit(
      "approve-schedule",
      () => actions.approve(a.id, { publish: "schedule", scheduled_for: whenIso }),
      "Article scheduled",
    );
  };

  const onRequestChanges = () => {
    if (!note.trim()) {
      toast("Add a note explaining what needs changing", { tone: "error" });
      return;
    }
    void runAndExit(
      "request-changes",
      () => actions.requestChanges(a.id, note),
      "Changes requested — author notified",
    );
  };

  const onReject = () => {
    if (!note.trim()) {
      toast("Add a note explaining the rejection", { tone: "error" });
      return;
    }
    void runAndExit(
      "reject",
      () => actions.reject(a.id, note),
      "Article rejected",
    );
  };

  const onPostNote = async () => {
    const body = activityNote.trim();
    if (!body || busy) return;
    setBusy("note");
    try {
      await actions.addNote(a.id, body);
      toast("Note posted", { tone: "success" });
      setActivityNote("");
      refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast(`Couldn't post note: ${msg}`, { tone: "error" });
    } finally {
      setBusy(null);
    }
  };

  // Schedule modal defaults: now + 1h (rounded to next 5 min), bounded
  // to [now+5min, now+90d] to match approveArticle backend validation.
  const defaultScheduleISO = (() => {
    const t = new Date(Date.now() + 3600_000);
    t.setSeconds(0, 0);
    t.setMinutes(Math.ceil(t.getMinutes() / 5) * 5);
    return t.toISOString();
  })();
  const minISO = new Date(Date.now() + 5 * 60_000).toISOString();
  const maxISO = new Date(Date.now() + 90 * 86_400_000).toISOString();

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0 }}>
      {/* ── Article (left) ───────────────────────────────────── */}
      <div style={left}>
        <div style={metaBar}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              style={crumbBtn}
              title="Back to queue"
            >
              {Icon.arrowL} Back
            </button>
            <KindChip kind={a.kind} />
            <ReviewPill state={a.state} />
            <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--ink-3)" }}>
              v{a.version} · /news/{a.id}
            </span>
          </div>
          {/*
            Header tools (version-diff, full-article preview, inline edit)
            were stubs; reinstate them once the underlying flows exist
            (revisions endpoint, public preview route, slate/lexical editor).
          */}
        </div>

        <article style={articleStyle}>
          <header>
            <div
              style={{
                fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-4)",
                letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 600,
              }}
            >
              {author.team} · Quarterly read
            </div>
            {detail?.wire?.hero_image && (
              <img
                src={detail.wire.hero_image}
                alt=""
                style={{ width: "100%", borderRadius: 10, margin: "14px 0 0", display: "block" }}
              />
            )}
            <h1 style={h1Style}>{a.title}</h1>
            <p style={dekStyle}>{a.dek}</p>
            <div
              style={{
                display: "flex", alignItems: "center", gap: 10, marginTop: 16,
                paddingTop: 14, borderTop: "1px solid var(--rule)",
              }}
            >
              <Avatar initials={author.initials} tint={author.tint} size={32} title={author.name} />
              <div>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink)", fontWeight: 500 }}>
                  {author.name}
                </div>
                <div style={{ fontFamily: "var(--font-ui)", fontSize: 11, color: "var(--ink-3)" }}>
                  {author.team} · {a.words.toLocaleString()} words · {a.readTime} read
                </div>
              </div>
            </div>
          </header>

          <p style={pStyle}>
            Caveats lodged in Q1 2026 came in flat against Q4 — but the price line tells a different
            story. Across CCR and RCR, median PSF crept up between 1.5% and 2.1% on a like-for-like
            basis. The divergence between price and volume is what's worth reading carefully.
          </p>

          <h2 style={h2Style} id="ccr-rcr">CCR vs RCR: where the price moved</h2>
          <p style={pStyle}>
            Two effects are stacking. First, the CCR's volume <strong>declined modestly</strong> while
            median PSF rose 1.8% — a thin-market signal that small numbers of high-PSF transactions
            are doing disproportionate work. Second, the RCR saw <em>both</em> volume and PSF inch
            up, which is the cleaner read: real demand returning in mid-tier districts.
          </p>

          <HighlightedP id="anchor-jld">
            The Jurong Lake District resale market is the bellwether here. Resale PSF in D22 is up
            5.4% over six months, ahead of the islandwide median by roughly 220 bps. URA's Draft
            Master Plan 2025 confirmed a 20% GFA uplift for residential plots in the precinct.
          </HighlightedP>

          <h2 style={h2Style}>What we can verify, what we cannot</h2>
          <p style={pStyle}>
            The PSF figures above come from caveats lodged with URA between Jan 1 and Mar 31, 2026.
            Volume comparisons are like-for-like and seasonally adjusted; the 5.4% JLD figure is
            from the same dataset.
          </p>

          <div style={footnoteStyle}>
            <strong>Sources:</strong> URA Caveats Data (Q1 2026, accessed 5 May 2026) ·
            URA Draft Master Plan 2025 (Apr 2025 factsheet) ·
            MAS Financial Stability Review (Nov 2025) ·
            IRAS Stamp Duty Statistics (Q1 2026).
          </div>
        </article>
      </div>

      {/* ── Review panel (right) ─────────────────────────────── */}
      <aside style={right}>
        <div style={panelBlock}>
          <div style={kicker}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#2a5b94" }} />
            In your court · claimed 17h ago
          </div>
          <h3 style={panelH}>Editorial decision</h3>

          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: 12 }}>
            <button
              style={{ ...approveBtn, opacity: busy ? 0.6 : 1 }}
              type="button"
              disabled={!!busy}
              onClick={onApproveNow}
            >
              {Icon.check} Approve &amp; publish now
            </button>
            <button
              style={{ ...scheduleBtn, opacity: busy ? 0.6 : 1 }}
              type="button"
              disabled={!!busy}
              onClick={() => setScheduling(true)}
            >
              {Icon.cal} Approve &amp; schedule…
            </button>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <button
                style={{ ...changesBtn, opacity: busy ? 0.6 : 1 }}
                type="button"
                disabled={!!busy}
                onClick={onRequestChanges}
              >
                {Icon.arrowL} Changes
              </button>
              <button
                style={{ ...rejectBtn, opacity: busy ? 0.6 : 1 }}
                type="button"
                disabled={!!busy}
                onClick={onReject}
              >
                {Icon.x} Reject
              </button>
            </div>
          </div>

          <div style={{ marginTop: 14 }}>
            <div
              style={{
                fontFamily: "var(--font-ui)", fontSize: 10, color: "var(--ink-4)",
                letterSpacing: "0.13em", textTransform: "uppercase", fontWeight: 600,
              }}
            >
              Note to author (required for Changes / Reject)
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Looks good — one source citation tightened up. Approving on the next pass…"
              style={{
                marginTop: 6, padding: "10px 12px", minHeight: 64, width: "100%",
                borderRadius: 7, border: "1px solid var(--rule)",
                background: "var(--bg)", fontFamily: "var(--font-ui)",
                fontSize: 12.5, color: "var(--ink-2)",
                resize: "vertical", outline: "none",
              }}
            />
          </div>
        </div>

        <div style={panelBlock}>
          <h3 style={panelH}>Automated checks</h3>
          <div style={{ marginTop: 10 }}>
            {EDITORIAL_CHECKS.map((c) => (
              <div key={c.id} style={checkRow}>
                <span
                  style={{
                    width: 16, height: 16, borderRadius: 999, flexShrink: 0,
                    background: c.state === "pass" ? "rgba(29,74,58,0.10)" : "rgba(138,90,0,0.15)",
                    color: c.state === "pass" ? "#1d4a3a" : "#8a5a00",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontSize: 9.5, fontWeight: 700,
                  }}
                >
                  {c.state === "pass" ? "✓" : "!"}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: "var(--ink)", fontWeight: 500 }}>{c.label}</div>
                  <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 1 }}>{c.detail}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={panelBlock}>
          <h3 style={panelH}>Submission</h3>
          <Row label="Author">
            {authorId && !isHouseAuthor ? (
              <button
                type="button"
                onClick={() => setOpenAuthor(true)}
                style={authorChipBtn}
                title="Open author profile"
              >
                <Avatar initials={author.initials} tint={author.tint} size={18} title={author.name} />
                <span style={{ textDecoration: "underline", textUnderlineOffset: 2 }}>{author.name}</span>
              </button>
            ) : (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Avatar initials={author.initials} tint={author.tint} size={18} title={author.name} /> {author.name}
              </span>
            )}
          </Row>
          {detail?.draftedBy && <Row label="Drafted by">{detail.draftedBy.name}</Row>}
          <Row label="Desk">{author.team}</Row>
          <Row label="Version" mono>v{a.version} · {a.version} revision{a.version === 1 ? "" : "s"}</Row>
          <Row label="Submitted">{a.submitted}</Row>
          <Row label="Word count" mono>{a.words.toLocaleString()}</Row>
          <Row label="Slug" mono>{a.slug}</Row>
          <Row label="Audience">All buyers · CCR &amp; RCR cohorts</Row>
        </div>

        <div style={panelBlock}>
          <h3 style={panelH}>Review activity</h3>
          <div style={{ marginTop: 12 }}>
            {REVIEW_ACTIVITY.map((ev, i) => <ActivityRow key={i} ev={ev} />)}
          </div>

          <div style={{ marginTop: 14, padding: 12, borderRadius: 8, background: "var(--bg)", border: "1px solid var(--rule)" }}>
            <textarea
              value={activityNote}
              onChange={(e) => setActivityNote(e.target.value)}
              placeholder="Leave a comment for the author or other reviewers…"
              style={{
                width: "100%", minHeight: 56, resize: "vertical",
                border: "none", outline: "none", background: "transparent",
                fontFamily: "var(--font-ui)", fontSize: 12.5, color: "var(--ink)",
                lineHeight: 1.5,
              }}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  void onPostNote();
                }
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
              <span style={{ fontFamily: "var(--font-ui)", fontSize: 10.5, color: "var(--ink-4)" }}>
                ⌘↵ to post · @mention
              </span>
              <button
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", borderRadius: 6,
                  background: "var(--accent)", border: "none",
                  color: "#f4f1ea",
                  cursor: activityNote.trim() && !busy ? "pointer" : "not-allowed",
                  opacity: activityNote.trim() && !busy ? 1 : 0.5,
                  fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500,
                }}
                type="button"
                disabled={!activityNote.trim() || !!busy}
                onClick={onPostNote}
              >
                {Icon.send} {busy === "note" ? "Posting…" : "Post note"}
              </button>
            </div>
          </div>
        </div>
      </aside>

      <ScheduleModal
        open={scheduling}
        defaultISO={defaultScheduleISO}
        minISO={minISO}
        maxISO={maxISO}
        onClose={() => setScheduling(false)}
        onSubmit={onApproveSchedule}
        busy={busy === "approve-schedule"}
      />

      {openAuthor && authorId && (
        <AuthorDrawer authorId={authorId} onClose={() => setOpenAuthor(false)} />
      )}
    </div>
  );
}

/* ─── ScheduleModal ──────────────────────────────────────────────── */

function ScheduleModal({
  open, defaultISO, minISO, maxISO, onClose, onSubmit, busy,
}: {
  open: boolean;
  defaultISO: string;
  minISO: string;
  maxISO: string;
  onClose: () => void;
  onSubmit: (iso: string) => void | Promise<void>;
  busy: boolean;
}) {
  const [iso, setIso] = useState(defaultISO);
  // Reset to the default whenever the modal re-opens so a previous
  // value doesn't bleed across sessions.
  useEffect(() => {
    if (open) setIso(defaultISO);
  }, [open, defaultISO]);

  const valid = (() => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return t >= new Date(minISO).getTime() && t <= new Date(maxISO).getTime();
  })();

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Schedule publish"
      width={460}
      footer={
        <>
          <button type="button" style={ghostBtn} onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: !valid || busy ? 0.5 : 1 }}
            disabled={!valid || busy}
            onClick={() => onSubmit(iso)}
          >
            {busy ? "Scheduling…" : "Approve & schedule"}
          </button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <DateTimeField
          value={iso || defaultISO}
          onChange={setIso}
          min={minISO}
          max={maxISO}
          label="Publish at"
        />
        <div
          style={{
            padding: "10px 12px", borderRadius: 6,
            background: "var(--surface-2)", border: "1px solid var(--rule)",
            color: "var(--ink-3)", fontSize: 12.5, lineHeight: 1.55,
          }}
        >
          Between 5 minutes and 90 days from now. The article moves to <strong>Scheduled</strong> until
          the cron flips it live. You can cancel by re-opening the article.
        </div>
      </div>
    </Modal>
  );
}

function HighlightedP({ id, children }: { id: string; children: ReactNode }) {
  return (
    <div id={id} style={{ position: "relative", margin: "14px 0" }}>
      <p
        style={{
          ...pStyle,
          margin: 0,
          background: "rgba(255, 220, 110, 0.20)",
          padding: "10px 14px 10px 16px",
          borderRadius: 6,
          borderLeft: "3px solid #b07c1a",
        }}
      >
        {children}
      </p>
      <div
        style={{
          position: "absolute", right: -10, top: -8,
          display: "inline-flex", alignItems: "center", gap: 5,
          background: "#fbf6ec", border: "1px solid #d9c896",
          padding: "2px 8px 2px 6px", borderRadius: 999,
          fontFamily: "var(--font-ui)", fontSize: 10.5, color: "#6b5320", fontWeight: 600,
        }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a8 8 0 1 1-3.5-6.6L21 4l-1 4.5A8 8 0 0 1 21 12z" />
        </svg>
        2 comments · resolved
      </div>
    </div>
  );
}

function Row({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    <div
      style={{
        display: "flex", justifyContent: "space-between", gap: 12,
        padding: "6px 0", borderTop: "1px solid var(--rule)",
        fontFamily: "var(--font-ui)", fontSize: 12, lineHeight: 1.4,
      }}
    >
      <span style={{ color: "var(--ink-3)", flexShrink: 0 }}>{label}</span>
      <span
        style={{
          color: "var(--ink-2)", textAlign: "right",
          fontFamily: mono ? "var(--font-mono)" : "inherit",
          fontSize: mono ? 11.5 : 12,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}
      >
        {children}
      </span>
    </div>
  );
}

function ActivityRow({ ev }: { ev: ActivityEvent }) {
  // Activity rows still come from the mock REVIEW_ACTIVITY array, so
  // `ev.who` is a mock AuthorKey. Once we render events from the
  // /admin/articles/{id} response (`events[]`), `who` becomes a real
  // auth_users UUID and the AUTHORS lookup will miss — guard with a
  // fallback chip so we never undefined.initials again.
  const fallback = { name: "Unknown", initials: "?", tint: "#6e7974" };
  const who =
    ev.who === "system" ? null
  : ev.who === "you"    ? { name: "You", initials: "HK", tint: "#1d4a3a" }
  :                       (AUTHORS[ev.who as AuthorKey] ?? fallback);
  const isComment = ev.kind === "comment";

  return (
    <div style={{ display: "flex", gap: 10, padding: "10px 0", borderTop: "1px solid var(--rule)" }}>
      <div style={{ flexShrink: 0, paddingTop: 1 }}>
        {who ? (
          <Avatar initials={who.initials} tint={who.tint} size={24} title={who.name} />
        ) : (
          <span
            style={{
              width: 24, height: 24, borderRadius: 999,
              background: "var(--surface-2)", color: "var(--ink-3)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              fontFamily: "var(--font-mono)", fontSize: 11,
            }}
          >
            ⚙
          </span>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 7, fontFamily: "var(--font-ui)", fontSize: 11.5 }}>
          <span style={{ color: "var(--ink)", fontWeight: 500 }}>{who ? who.name : "Auto-checks"}</span>
          <span style={{ color: "var(--ink-4)" }}>·</span>
          <span style={{ color: "var(--ink-3)" }}>{ev.when}</span>
          {ev.anchor && (
            <span
              style={{
                fontFamily: "var(--font-mono)", fontSize: 10, color: "#6b5320",
                background: "#fbf6ec",
                padding: "0 6px", borderRadius: 4, border: "1px solid #d9c896",
              }}
            >
              {ev.anchor}
            </span>
          )}
        </div>
        <div
          style={{
            marginTop: 3, fontFamily: isComment ? "var(--font-serif)" : "var(--font-ui)",
            fontSize: isComment ? 13 : 12, color: "var(--ink-2)",
            lineHeight: 1.5,
            fontStyle: ev.kind === "flag-cleared" ? "italic" : "normal",
          }}
        >
          {ev.text}
        </div>
      </div>
    </div>
  );
}

/* ─── Styles ───────────────────────────────────────────────────── */
const left: CSSProperties = {
  flex: 1, minWidth: 0, display: "flex", flexDirection: "column",
  background: "var(--bg)", borderRight: "1px solid var(--rule)", overflow: "hidden",
};
const right: CSSProperties = {
  width: 380, flexShrink: 0, background: "var(--surface)", overflow: "auto", padding: 0,
};
const metaBar: CSSProperties = {
  flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "12px 28px", borderBottom: "1px solid var(--rule)", background: "var(--surface)",
};
const crumbBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 5,
  padding: "5px 11px 5px 8px", borderRadius: 6,
  background: "var(--bg)", border: "1px solid var(--rule)",
  color: "var(--ink-2)", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500,
};
const articleStyle: CSSProperties = {
  flex: 1, overflow: "auto", padding: "32px 56px 60px",
  maxWidth: 760, margin: "0 auto", width: "100%",
};
const h1Style: CSSProperties = {
  fontFamily: "var(--font-serif)", fontSize: 30, fontWeight: 500,
  letterSpacing: "-0.022em", margin: "12px 0 12px", color: "var(--ink)",
  lineHeight: 1.18,
};
const dekStyle: CSSProperties = {
  fontFamily: "var(--font-serif)", fontSize: 16, fontStyle: "italic",
  color: "var(--ink-2)", lineHeight: 1.5, margin: 0,
};
const h2Style: CSSProperties = {
  fontFamily: "var(--font-serif)", fontSize: 19, fontWeight: 500,
  letterSpacing: "-0.018em", margin: "26px 0 8px", color: "var(--ink)",
};
const pStyle: CSSProperties = {
  fontFamily: "var(--font-serif)", fontSize: 14.5, lineHeight: 1.7,
  color: "var(--ink-2)", margin: "10px 0",
};
const footnoteStyle: CSSProperties = {
  marginTop: 22, padding: "12px 16px",
  background: "var(--surface)", border: "1px solid var(--rule)", borderRadius: 8,
  fontFamily: "var(--font-ui)", fontSize: 11.5, color: "var(--ink-3)", lineHeight: 1.55,
};
const panelBlock: CSSProperties = {
  padding: "18px 20px", borderBottom: "1px solid var(--rule)",
};
const kicker: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  fontFamily: "var(--font-ui)", fontSize: 10.5, color: "#2a5b94",
  fontWeight: 600, letterSpacing: "0.06em",
};
const panelH: CSSProperties = {
  fontFamily: "var(--font-serif)", fontSize: 16, fontWeight: 500,
  letterSpacing: "-0.018em", margin: "6px 0 0", color: "var(--ink)",
};
const approveBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
  padding: "10px 12px", borderRadius: 8,
  background: "var(--accent)", border: "none",
  color: "#f4f1ea", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 500,
};
const scheduleBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
  padding: "9px 12px", borderRadius: 8,
  background: "var(--bg)", border: "1px solid var(--accent)",
  color: "var(--accent)", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 12.5, fontWeight: 500,
};
const changesBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  padding: "8px 10px", borderRadius: 7,
  background: "var(--bg)", border: "1px solid var(--rule)",
  color: "#8a5a00", cursor: "pointer",
  fontFamily: "var(--font-ui)", fontSize: 11.5, fontWeight: 500,
};
const rejectBtn: CSSProperties = { ...changesBtn, color: "#7a2d1f" };
const checkRow: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10,
  padding: "7px 0", borderTop: "1px solid var(--rule)",
};
const authorChipBtn: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 6,
  background: "transparent", border: "none", padding: 0,
  cursor: "pointer", color: "var(--ink-2)",
  fontFamily: "inherit", fontSize: "inherit",
};
